import React, { useState, useEffect, useRef } from 'react';
import { Wand2, Music, Sliders, LayoutGrid, Sparkles, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { API_BASE as API } from '../../lib/apiBase';

const CATEGORY_META = {
  sound_add:    { icon: Music,       color: '#F5C842', label_es: 'Sonido',    label_en: 'Sound' },
  sound_design: { icon: Wand2,       color: '#A855F7', label_es: 'Diseno',    label_en: 'Design' },
  arrangement:  { icon: LayoutGrid,  color: '#06B6D4', label_es: 'Arreglo',   label_en: 'Arrange' },
  mixing:       { icon: Sliders,     color: '#10B981', label_es: 'Mezcla',    label_en: 'Mix' },
};

export default function SmartCreativePanel({
  sessionId, sections, loopRegion, onExecuteAction, token
}) {
  const { lang } = useLang();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [executing, setExecuting] = useState(null);
  const fetchedFor = useRef(null);

  // Fetch actions when sessionId becomes available or changes
  useEffect(() => {
    if (!sessionId || !token) return;
    if (fetchedFor.current === sessionId) return;
    fetchedFor.current = sessionId;

    const doFetch = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/producer/smart-actions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            session_id: sessionId,
            sections: sections || [],
            loop_region: loopRegion || null,
          }),
        });
        const data = await res.json();
        if (data.actions && data.actions.length > 0) {
          setActions(data.actions);
        }
      } catch (e) {
        console.error('Smart actions fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    doFetch();
  }, [sessionId, token, sections, loopRegion]);

  const refreshActions = async () => {
    if (!sessionId || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/producer/smart-actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          session_id: sessionId,
          sections: sections || [],
          loop_region: loopRegion || null,
        }),
      });
      const data = await res.json();
      if (data.actions && data.actions.length > 0) {
        setActions(data.actions);
      }
    } catch (e) {
      console.error('Smart actions refresh error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async (action) => {
    setExecuting(action.id);
    // Learn the preference (non-blocking)
    try {
      if (action.learn_type && action.learn_value) {
        fetch(`${API}/api/producer/preferences/learn`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            session_id: sessionId,
            action_type: action.learn_type,
            value: action.learn_value,
          }),
        }).catch(() => {});
      }
    } catch (e) { /* non-blocking */ }

    // Execute the real iteration
    if (onExecuteAction) {
      await onExecuteAction(action.instruction);
    }
    setExecuting(null);
    // Refresh actions after execution for fresh context
    fetchedFor.current = null;
  };

  const grouped = {};
  actions.forEach(a => {
    const cat = a.category || 'sound_add';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(a);
  });

  if (!sessionId) return null;

  return (
    <div data-testid="smart-creative-panel" className="border-t border-[#1A1A1F]">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#111114] transition-colors"
        data-testid="smart-panel-toggle"
      >
        <div className="flex items-center gap-2">
          <Sparkles size={13} className="text-[#F5C842]" />
          <span className="text-[10px] font-bold tracking-widest text-[#A1A1AA] uppercase">
            {lang === 'es' ? 'Acciones creativas' : 'Creative Actions'}
          </span>
          {loading && <Loader2 size={10} className="text-[#F5C842] animate-spin" />}
        </div>
        {expanded ? <ChevronUp size={12} className="text-[#52525B]" /> : <ChevronDown size={12} className="text-[#52525B]" />}
      </button>

      {expanded && (
        <div className="px-2 pb-2 space-y-2">
          {Object.entries(grouped).map(([cat, catActions]) => {
            const meta = CATEGORY_META[cat] || CATEGORY_META.sound_add;
            const Icon = meta.icon;
            const catLabel = lang === 'es' ? meta.label_es : meta.label_en;
            return (
              <div key={cat}>
                <div className="flex items-center gap-1.5 px-1 mb-1">
                  <Icon size={10} style={{ color: meta.color }} />
                  <span className="text-[8px] uppercase tracking-[0.2em] font-semibold" style={{ color: meta.color }}>
                    {catLabel}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {catActions.map(action => {
                    const isExec = executing === action.id;
                    return (
                      <button
                        key={action.id}
                        data-testid={`smart-action-${action.id}`}
                        onClick={() => handleExecute(action)}
                        disabled={isExec || executing !== null}
                        title={action.instruction}
                        className="relative group px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all duration-200 border hover:border-opacity-60"
                        style={{
                          borderColor: isExec ? meta.color : '#27272A',
                          background: isExec ? `${meta.color}15` : '#0D0D10',
                          color: isExec ? meta.color : '#D4D4D8',
                        }}
                      >
                        {isExec ? (
                          <span className="flex items-center gap-1">
                            <Loader2 size={10} className="animate-spin" />
                            {lang === 'es' ? 'Aplicando...' : 'Applying...'}
                          </span>
                        ) : (
                          <span className="group-hover:text-white transition-colors">{action.label}</span>
                        )}
                        <span
                          className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                          style={{ boxShadow: `inset 0 0 12px ${meta.color}20, 0 0 8px ${meta.color}10` }}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Empty state */}
          {!loading && actions.length === 0 && (
            <p className="text-[10px] text-[#3F3F46] text-center py-2">
              {lang === 'es' ? 'Genera la base para desbloquear acciones' : 'Generate the base to unlock actions'}
            </p>
          )}

          {/* Refresh */}
          {!loading && actions.length > 0 && (
            <button
              onClick={refreshActions}
              className="w-full mt-1 py-1.5 text-[9px] text-[#52525B] hover:text-[#F5C842] transition-colors uppercase tracking-widest"
              data-testid="smart-panel-refresh"
            >
              {lang === 'es' ? 'Nuevas sugerencias' : 'New suggestions'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
